import { useEffect, useMemo, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { sql, MSSQL } from '@codemirror/lang-sql';
import DataGrid, { Column, Paging, Scrolling, Editing, HeaderFilter, Sorting, Export, Selection, SearchPanel } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { Resizable } from 'devextreme-react/resizable';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { runTask } from '../../images/icons';
import { SqlTab, SqlRowEdit } from '../../types/types.sqlEditor';

interface Props {
  tab: SqlTab;
  initialSql: string;
  schemaForCM: Record<string, string[]>;
  unlockToken: string | null;
  onPersistSql: (key: string, sql: string) => void;
  onRunSelect: (key: string, sql: string) => void;
  onRunWrite: (key: string, sql: string) => void;
  onRunDdl: (key: string, sql: string) => void;
  onSaveEdits: (table: string, edits: SqlRowEdit[], tabKey: string) => Promise<boolean>;
  onCancel: (key: string) => void;
  requestStepUp: () => void;
}

const WRITE_KEYWORDS = ['UPDATE', 'DELETE', 'INSERT'];
const DDL_KEYWORDS = ['ALTER', 'CREATE'];

export const QueryTab = ({ tab, initialSql, schemaForCM, unlockToken, onPersistSql, onRunSelect, onRunWrite, onRunDdl, onSaveEdits, onCancel, requestStepUp }: Props) => {
  const gridRef = useRef<DataGrid>(null);
  const result = tab.result;

  // El texto se maneja en estado LOCAL para no re-renderizar el TabPanel en cada tecla
  // (eso remontaba el editor y hacía perder el foco). Se persiste (en el ref del padre) al
  // salir del foco / ejecutar. 'initialSql' proviene de ese ref, así se conserva al remontar.
  const [localSql, setLocalSql] = useState(initialSql);
  useEffect(() => setLocalSql(initialSql), [tab.key]); // al cambiar de pestaña, refresca

  // Alto del editor cuando hay panel de resultados (arrastrable). Sin resultados el editor llena todo.
  const [editorHeight, setEditorHeight] = useState(200);

  // El panel de resultados solo existe cuando hay una consulta en curso o ya ejecutada.
  const showResults = tab.running || !!result;

  const editable = !!result?.EditableTable && (result?.PrimaryKey?.length ?? 0) > 0;
  const primaryKey = result?.PrimaryKey ?? [];
  const keyExpr = editable ? (primaryKey.length === 1 ? primaryKey[0] : primaryKey) : undefined;

  const extensions = useMemo(
    () => [sql({ dialect: MSSQL, schema: schemaForCM, upperCaseKeywords: true })],
    [schemaForCM]
  );

  const pkFromKey = (key: any): Record<string, any> => {
    if (primaryKey.length === 1) return { [primaryKey[0]]: key };
    return key; // clave compuesta => DevExtreme entrega un objeto
  };

  // Persiste ediciones del grid: actualiza (update), inserta (insert) y elimina (remove),
  // fila por fila. Todas exigen el token de segundo factor.
  const onSaving = (e: any) => {
    const changes = e.changes || [];
    if (changes.length === 0) return;
    if (!unlockToken) {
      e.cancel = true;
      requestStepUp();
      return;
    }
    const edits: SqlRowEdit[] = changes.map((c: any) => {
      if (c.type === 'insert') return { Op: 'insert', Pk: {}, Changes: c.data || {} };
      if (c.type === 'remove') return { Op: 'delete', Pk: pkFromKey(c.key), Changes: {} };
      return { Op: 'update', Pk: pkFromKey(c.key), Changes: c.data };
    });
    e.promise = onSaveEdits(result!.EditableTable as string, edits, tab.key).then((ok) => {
      if (!ok) return Promise.reject();
    });
  };

  // Un solo botón «Ejecutar»: decide SELECT / escritura / DDL de vista por la primera palabra.
  const run = () => {
    onPersistSql(tab.key, localSql);
    const first = localSql.trim().split(/\s+/)[0]?.toUpperCase();
    if (first && DDL_KEYWORDS.includes(first)) onRunDdl(tab.key, localSql);
    else if (first && WRITE_KEYWORDS.includes(first)) onRunWrite(tab.key, localSql);
    else onRunSelect(tab.key, localSql);
  };

  // Descarga a Excel lo que se está mostrando en el grid de resultados.
  const onExporting = (e: any) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Resultados');
    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    })
      .then(() => workbook.xlsx.writeBuffer())
      .then((buffer) => {
        const date = new Date().toISOString().slice(0, 10);
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${tab.title} - ${date}.xlsx`);
      });
  };

  const editor = (
    <div className="sqle-editor-wrap">
      <CodeMirror
        className="sqle-cm"
        value={localSql}
        height="100%"
        theme="light"
        extensions={extensions}
        onChange={(val) => setLocalSql(val)}
        onBlur={() => onPersistSql(tab.key, localSql)}
      />
    </div>
  );

  return (
    <div className="sqle-tab">
      {showResults ? (
        <Resizable
          className="sqle-editor-resizable"
          handles="bottom"
          height={editorHeight}
          minHeight={100}
          onResize={(e) => setEditorHeight(e.height)}
        >
          {editor}
        </Resizable>
      ) : (
        <div className="sqle-editor-fill">{editor}</div>
      )}

      <div className="sqle-actions">
        <Button icon={runTask} text="Ejecutar" type="default" stylingMode="contained" disabled={tab.running} onClick={run} />
        <Button icon="close" text="Detener" type="danger" stylingMode="contained" disabled={!tab.running} onClick={() => onCancel(tab.key)} />
        <div className="sqle-status">
          {tab.running && <span>Ejecutando…</span>}
          {!tab.running && result && (
            <span>
              {result.RowCount} fila(s){result.Truncated ? ` (truncado a ${result.RowCount})` : ''} · {result.ElapsedMs} ms
              {editable ? ` · editable` : ' · solo lectura'}
            </span>
          )}
          {tab.error && <span className="sqle-error">{tab.error}</span>}
        </div>
      </div>

      {showResults && (
      <div className="sqle-grid-wrap">
        {result && (
          <DataGrid
            ref={gridRef}
            dataSource={result.Rows}
            keyExpr={keyExpr as any}
            height="100%"
            width="100%"
            showBorders
            columnAutoWidth
            allowColumnResizing
            hoverStateEnabled
            onSaving={onSaving}
            onExporting={onExporting}
          >
            <Export enabled={true} />
            <SearchPanel visible={true} placeholder="Buscar en resultados…" width={240} />
            <Selection mode="single" />
            <Sorting mode="multiple" />
            <HeaderFilter visible />
            <Scrolling mode="virtual" columnRenderingMode="virtual" />
            <Paging pageSize={50} />
            <Editing
              mode="batch"
              allowUpdating={editable}
              allowAdding={editable}
              allowDeleting={editable}
              useIcons
              selectTextOnEditStart
              startEditAction="dblClick"
            />
            {result.Columns.map((col) => (
              <Column
                key={col.Name}
                dataField={col.Name}
                caption={col.Name}
                allowEditing={editable && !primaryKey.includes(col.Name)}
              />
            ))}
          </DataGrid>
        )}
        {!result && tab.running && (
          <div className="sqle-empty">Ejecutando…</div>
        )}
      </div>
      )}
    </div>
  );
};

export default QueryTab;
